import { useLocation, useSearchParams } from "react-router-dom"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import { IModelMetaDataCard, IModelMetaDataColumn } from "../../util/interface"
import { ModelMetaDataCard } from "./ModelMetaDataCard"
import { visuallyHidden } from "@mui/utils"
import {
  cardsAsync,
  chartDataAsync,
  resetState,
  tabChange,
  tableDataAsync,
} from "./ModelMetaDataSlice"
import React, { useEffect, useState } from "react"
import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { Box, Divider, TableSortLabel } from "@mui/material"
// import SingleSelection from "../../components/filters/SingleSelection"
import { MODEL_META_DATA_CONFIGS, SCREENS_DETAILS } from "../../util/StaticData"
import {
  marketSinleSelectedValueSelector,
  pipeLineNamesAndRunIdsAsync,
  pipeLineSelectedValueSelector,
  runIdChange,
  updateStartAndEndDateBasedOnRunId,
  useCaseSelectedValueSelector,
} from "../Monitor/FilterSlice"
import {
  ABBREVIATE_NUMBER,
  getCurrentParamsInObj,
} from "../../util/CommonMethods"
import HighChartsLineChart from "./HighChartsLineChart"
import { ErrorBoundary } from "react-error-boundary"
import { CustomErrorMsg } from "../../components/CustomErrorMsg"

const cols: IModelMetaDataColumn[] = [
  { id: "CATEGORY_UPDATED", label: "", minWidth: 60 },
  {
    id: "volume_mix",
    label: "",
    minWidth: 60,
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "HM CC_before",
    label: "Before Retraining",
    minWidth: 60,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "HM CC_after",
    label: "After Retraining",
    align: "center",
    minWidth: 60,
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "HM CT_before",
    label: "Before Retraining",
    minWidth: 60,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "HM CT_after",
    label: "After Retraining",
    minWidth: 60,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "OP_before",
    label: "Before Retraining",
    minWidth: 60,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
  {
    id: "OP_after",
    label: "After Retraining",
    minWidth: 60,
    align: "center",
    format: (value: number) => ABBREVIATE_NUMBER(value, 2),
  },
]
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

type Order = "asc" | "desc"

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key,
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string },
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}
function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number,
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) {
      return order
    }
    return a[1] - b[1]
  })
  return stabilizedThis.map((el) => el[0])
}

const colBorder2 = [0, 1, 2, 3, 4, 5, 6]

const useStyles = createUseStyles({
  content: {
    display: "grid",
    gridTemplateColumns: "1fr 10px 1fr",
    background: "#FFFFFF",
    padding: 10,
  },
  overAllWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  commonFont: {},
  chartHeader: {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "19.5px",
    letterSpacing: "0.4375px",
    textAlign: "center",
  },
  sideHeader: {
    fontSize: "14px",
    fontStyle: "italic",
    fontWeight: 400,
    lineHeight: "17.07px",
    textAlign: "left",
  },
  miniHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tableWrapper: {
    display: "grid",
    gap: 15,
    padding: 20,
  },
  chartWrapper: {
    display: "grid",
    gap: 15,
    padding: 20,
    gridTemplateRows: "5% 5% 2% 88%",
    maxHeight: 477,
  },
  filter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  parentHeader: {
    background: "var(--primary-dp-color-20) !important",
    color: "var(--text-black-color) !important",
    fontWeight: "700 !important",
  },
  colHeader: {
    background: "#F4F4F4 !important",
    color: "#7D7D7D !important",
  },
})
export const ModelMetaData = () => {
  const theme: any = useTheme()
  const classes = useStyles({ theme })
  const tableData = useAppSelector((store) => store.modelMetaData.tableData)
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const tabs = useAppSelector((state) => state.modelMetaData.tabs)
  // local state
  const [order, setOrder] = React.useState<Order>("asc")
  const [orderBy, setOrderBy] = React.useState("calories")

  const activeTab = useAppSelector((store) => store.modelMetaData.activeTab)
  const [tableColumns] = useState(cols)
  const dispatch = useAppDispatch()
  const tabClick = (obj: IModelMetaDataCard) => {
    dispatch(tabChange(obj.key))
    const tempParams: any = getCurrentParamsInObj(searchParams)
    const params = {
      ...tempParams,
      runId: searchParams.get("runId"),
      tab: obj.key,
    }
    if (!params.runId) {
      delete params.runId
    }
    setSearchParams(params)
  }
  useEffect(() => {
    const tab: any = searchParams.get("tab")
    const runId: any = searchParams.get("runId")
    if (runId) {
      dispatch(runIdChange(runId))
    }
    dispatch(tabChange(tab))
  }, [location])
  // local state for pagination
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(5)

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }
  const selectedRunIdFromMonitoring = useAppSelector(
    (store) =>
      store.monitor.pipelineRunDialog?.selectedRunDetails?.az_pipeline_run_id,
  )

  useEffect(() => {
    if (selectedRunIdFromMonitoring) {
      const tempParams: any = getCurrentParamsInObj(searchParams)
      setSearchParams({
        ...tempParams,
        runId: selectedRunIdFromMonitoring,
        ref: SCREENS_DETAILS.MONITORING,
      })
    }
  }, [selectedRunIdFromMonitoring])

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }
  // selectors from the store
  const tableViewBySelectedValue = useAppSelector(
    (store) => store.modelMetaData.tableViewBySelectedValue,
  )
  const runIdSelectedValue = useAppSelector(
    (state) => state.filters.runIdSelectedValue,
  )
  //reset the previous data by default
  useEffect(() => {
    dispatch(resetState())
  }, [])

  const useCaseSelectedValue = useAppSelector(useCaseSelectedValueSelector)

  const marketSingleSelectedValue = useAppSelector(
    marketSinleSelectedValueSelector,
  )
  // get new data if there's a change in any dependencies
  useEffect(() => {
    if (
      runIdSelectedValue &&
      useCaseSelectedValue &&
      marketSingleSelectedValue
    ) {
      dispatch(cardsAsync())
    }
  }, [runIdSelectedValue, useCaseSelectedValue, marketSingleSelectedValue])
  useEffect(() => {
    if (
      runIdSelectedValue &&
      useCaseSelectedValue &&
      marketSingleSelectedValue
    ) {
      dispatch(tableDataAsync())
      dispatch(chartDataAsync())
    }
  }, [
    activeTab,
    runIdSelectedValue,
    useCaseSelectedValue,
    marketSingleSelectedValue,
  ])
  const pipeLineSelectedValue = useAppSelector(pipeLineSelectedValueSelector)
  const startDate = useAppSelector((state) => state.filters.startDate)
  const endDate = useAppSelector((state) => state.filters.endDate)
  useEffect(() => {
    const ref: any = searchParams.get("ref")
    if (!ref && !startDate && !endDate) {
      dispatch(updateStartAndEndDateBasedOnRunId())
    }
  }, [runIdSelectedValue, pipeLineSelectedValue])
  useEffect(() => {
    const runId: any = searchParams.get("runId")
    const ref: any = searchParams.get("ref")
    if (
      runId &&
      pipeLineSelectedValue &&
      runIdSelectedValue &&
      ref == "monitor"
    ) {
      if (!startDate && !endDate) {
        dispatch(updateStartAndEndDateBasedOnRunId())
      }
    }
  }, [location, pipeLineSelectedValue, runIdSelectedValue])

  const visibleRows = React.useMemo(
    () =>
      stableSort(tableData, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      ),
    [order, orderBy, page, rowsPerPage, tableData],
  )
  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {
    const isAsc = orderBy === property && order === "asc"
    setOrder(isAsc ? "desc" : "asc")
    setOrderBy(property)
  }
  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property)
    }
  const totalRowTableData: any = useAppSelector(
    (store) => store.modelMetaData.totalRowTableData,
  )
  useEffect(() => {
    if (useCaseSelectedValue && marketSingleSelectedValue) {
      dispatch(pipeLineNamesAndRunIdsAsync())
    }
  }, [useCaseSelectedValue, marketSingleSelectedValue])
  return (
    <div className={classes.overAllWrapper}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
        {tabs.map((obj: IModelMetaDataCard) => {
          return (
            <Box onClick={() => tabClick(obj)} key={obj.title} sx={{ flex: 1 }}>
              <ModelMetaDataCard
                activeTab={activeTab}
                cardDetails={obj}
              ></ModelMetaDataCard>
            </Box>
          )
        })}
      </Box>
      <div className={classes.content}>
        <div className={classes.chartWrapper}>
          <div className={classes.chartHeader}>
            {MODEL_META_DATA_CONFIGS?.[activeTab]?.chartHeader}
          </div>
          <div className={classes.filter}></div>
          <Divider />
          <Box sx={{ height: 500, minWidth: "25rem" }}>
            {/* Rendering line chart */}
            <ErrorBoundary fallback={<CustomErrorMsg />}>
              <HighChartsLineChart></HighChartsLineChart>
            </ErrorBoundary>
          </Box>
        </div>
        <Divider orientation="vertical" />
        <div className={classes.tableWrapper}>
          <div className={classes.chartHeader}>
            {MODEL_META_DATA_CONFIGS?.[activeTab]?.tableHeader}
          </div>
          <div className={classes.miniHeader}>
            <div className={classes.sideHeader}>
              Across Model / Portfolio Hierarchy
            </div>
            <div className={classes.filter}></div>
          </div>
          <Divider />

          <div className="table">
            <Paper sx={{ width: "100%" }}>
              {/* Rendering line table */}
              <TableContainer sx={{ minHeight: 380, fontFamily: "Monsterrat" }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        className={classes.commonFont}
                        align="center"
                        colSpan={1}
                        sx={{
                          borderBottom: "none",
                          position: "static",
                          background: "#F4F4F4",
                          borderRight: "1px solid lightgray",
                        }}
                      >
                        <Box
                          sx={{ top: 32, zIndex: 10, position: "relative" }}
                          className={classes.colHeader}
                        >
                          {tableViewBySelectedValue == "category"
                            ? "Category"
                            : "Region"}
                        </Box>
                      </TableCell>
                      <TableCell
                        className={classes.commonFont}
                        align="center"
                        colSpan={1}
                        sx={{
                          borderBottom: "none",
                          position: "static",
                          background: "#F4F4F4",
                          borderRight: "1px solid lightgray",
                        }}
                      >
                        <Box
                          sx={{ top: 32, zIndex: 10, position: "relative" }}
                          className={classes.colHeader}
                        >
                          Volume Mix
                        </Box>
                      </TableCell>
                      <TableCell
                        className={`${classes.commonFont} ${classes.parentHeader}`}
                        align="center"
                        colSpan={2}
                        sx={{
                          borderRight: "1px solid lightgray",
                        }}
                      >
                        MT
                      </TableCell>
                      <TableCell
                        className={`${classes.commonFont} ${classes.parentHeader}`}
                        align="center"
                        colSpan={2}
                        sx={{
                          borderRight: "1px solid lightgray",
                        }}
                      >
                        TT HM
                      </TableCell>
                      <TableCell
                        className={`${classes.commonFont} ${classes.parentHeader}`}
                        align="center"
                        colSpan={2}
                        sx={{
                          borderRight: "1px solid lightgray",
                        }}
                      >
                        TT OP
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {tableColumns.map((column, i) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          className={`${classes.commonFont}  ${classes.colHeader}`}
                          sx={{
                            top: 57,
                            minWidth: column.minWidth,
                            borderRight: colBorder2.includes(i)
                              ? "1px solid lightgray"
                              : "",
                          }}
                        >
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={createSortHandler(column.id)}
                          >
                            {column.label}
                            {orderBy === column.id ? (
                              <Box component="span" sx={visuallyHidden}>
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <ErrorBoundary fallback={<CustomErrorMsg />}>
                    <TableBody>
                      {visibleRows
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage,
                        )
                        .map((row: any, index: number) => {
                          return (
                            <TableRow
                              hover
                              role="checkbox"
                              tabIndex={-1}
                              key={index}
                            >
                              {tableColumns.map((column, i) => {
                                const value = row[column.id]
                                return (
                                  <TableCell
                                    className={classes.commonFont}
                                    key={column.id}
                                    align={column.align}
                                    sx={{
                                      borderRight: "1px solid lightgray",
                                      borderLeft:
                                        i == 0 ? "1px solid lightgray" : "",
                                    }}
                                  >
                                    {column.format && typeof value === "number"
                                      ? column.format(value)
                                      : value}
                                    {i !== 0 ? "%" : ""}
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          )
                        })}
                      {tableData.length ? (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={"Total"}
                        >
                          <TableCell
                            className={classes.commonFont}
                            align={"center"}
                            sx={{
                              borderRight: "1px solid lightgray",
                              borderLeft: "1px solid lightgray",
                              fontWeight: 700,
                            }}
                          >
                            NARTD
                          </TableCell>
                          {tableColumns
                            .filter((obj) => obj.id !== "CATEGORY_UPDATED")
                            .map((obj: IModelMetaDataColumn, i: number) => {
                              return (
                                <TableCell
                                  className={classes.commonFont}
                                  key={obj.id}
                                  align={"center"}
                                  sx={{
                                    borderLeft:
                                      i == 0 ? "1px solid lightgray" : "",
                                    borderRight: "1px solid lightgray",
                                    fontWeight: 700,
                                  }}
                                >
                                  {typeof totalRowTableData?.[obj.id] ===
                                  "number"
                                    ? ABBREVIATE_NUMBER(
                                        totalRowTableData?.[obj.id],
                                        2,
                                      )
                                    : ""}
                                  {/* {obj.id != "volume_mix" ? "%" : ""} */}%
                                </TableCell>
                              )
                            })}
                        </TableRow>
                      ) : (
                        ""
                      )}
                    </TableBody>
                  </ErrorBoundary>
                </Table>
              </TableContainer>
              {tableData.length ? (
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={tableData.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              ) : (
                ""
              )}
            </Paper>
          </div>
        </div>
      </div>
    </div>
  )
}
